import React, { useEffect } from "react";
import { Provider, rootStore } from "./stores/main";
import { ConfigProvider, Layout } from "antd";
import { BrowserRouter as Router, Route, useParams } from "react-router-dom";
import LogRocket from "logrocket";
import { TopBar } from "./components/playground-top-bar/TopBar";
import { useHistory } from "react-router-dom";
import { MainLayout } from "./components/MainLayout";
import { ToastContainer } from "react-toastify";
import { fetchUserAttributes } from "aws-amplify/auth";
import BlingsTheme from "./config/antdTheme";
import withSetupTOTPMFA from "./components/customAuth/withSetupTOTPMFA";
import { withDefaultSignIn } from "@blings/shared-auth-component";
import { Hub } from "aws-amplify/utils";
import { getCurrentUser, signIn, signOut } from "aws-amplify/auth";
import { getENV, info_data } from "./stores/consts";
import { ThemeProvider } from "@aws-amplify/ui-react";
import BrandedAuthenticator from "./components/customAuth/BrandedAuthenticator";
import { AUTH_COOKIE_SYNC_CONFIG } from "./components/customAuth/authCookieSyncConfig";
import "./App.scss";
import "./layout.scss";
import "./toastify.css";
import { parsePathName, tabsPathResolver } from "./utils/navigation";
import { del, get, post } from "aws-amplify/api";

const App: React.FC = () => {
  const history = useHistory();
  const location = parsePathName(window.location.pathname);

  const { projectId } = useParams<{
    projectId: string | undefined;
    scene: string | undefined;
  }>();

  // todo: combine both useEffect at once, with proper deps
  useEffect(() => {
    (async () => {
      // Ensure IoT policy is attached for the authenticated user at app start
      const attachIoTPolicy = async () => {
        await get({
          apiName: "BlingsAPI",
          path: "/v3/private/user/attach",
        }).response;
      };
      attachIoTPolicy().catch((error) => {
        console.error("Failed to attach IoT policy", error);
      });

      // Load account data first, then load projects (which needs the account ID)
      await rootStore.accountStore.loadAccountData();

      if (projectId) {
        await rootStore.platformStore.loadProject(projectId);
      } else {
        console.log(
          "No projectId in URL, loading all projects, wait for selection"
        );
        await rootStore.platformStore.loadProjects();
      }
      if (rootStore.platformStore.allProjects.length && projectId) {
        const scene = decodeURIComponent(location.scene);
        await rootStore.platformStore.selectProject(projectId, scene);
        rootStore.platformStore.setSelectedSceneName(scene);
        if (
          rootStore.platformStore.projectWorkspaceVersion &&
          rootStore.platformStore.projectWorkspaceVersion.videoParts?.length &&
          !location.scene
        ) {
          let route = "/" + tabsPathResolver(location);
          let selectedScene = rootStore.platformStore.selectedScene;
          if (!selectedScene) {
            selectedScene =
              rootStore.platformStore.projectWorkspaceVersion.videoParts[0]
                .name;
          }
          if (location.tab === "layers")
            route += `/${encodeURIComponent(selectedScene)}`;
          history.push(route);
        }
      }
    })();
    // rootStore.notificationStore.init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function listener(e: BeforeUnloadEvent) {
      const { hasUnsavedChanges } = rootStore.platformStore;
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "Latest changes have not been saved.";
        return "Latest changes have not been saved.";
      }
    }
    window.addEventListener("beforeunload", listener);
    return () => {
      window.removeEventListener("beforeunload", listener);
    };
  }, []);

  useEffect(() => {
    if (!rootStore.platformStore.initialProjectLoadSuccess) {
      console.log("No projects found");
      return;
    }
    (async () => {
      if (projectId !== rootStore.platformStore.selectedProjectId) {
        rootStore.platformStore.resetProjectStore();
        await rootStore.platformStore.selectProject(projectId);
      }
      if (location.scene) {
        rootStore.platformStore.changeVideoPart(
          decodeURIComponent(location.scene),
          false
        );
      } else if (
        rootStore.platformStore.projectWorkspaceVersion &&
        rootStore.platformStore.projectWorkspaceVersion.videoParts?.length
      ) {
        let route = "/" + tabsPathResolver(location);
        if (location.tab === "layers") {
          let selectedScene = rootStore.platformStore.selectedScene;
          if (!selectedScene) {
            selectedScene =
              rootStore.platformStore.projectWorkspaceVersion.videoParts[0]
                .name;
          }
          route += `/${encodeURIComponent(selectedScene)}`;
        } else {
          if (!location.tab) {
            rootStore.platformStore.setSelectedSceneName(null);
          }
        }
        history.push(route);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, location.scene]);

  useEffect(() => {
    rootStore.playerStore.setLayerInspectorMode("connect", {
      clearSelections: true,
    });
    rootStore.timelineStore.setCollapsed(true);
    rootStore.layoutStore.setLayerVisualPanelCollapsed(true);
    rootStore.layoutStore.setSchemaDrawerOpen(false);
    rootStore.layoutStore.setConnectorsPanelCollapsed(false);
  }, [projectId, location.scene]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      LogRocket.init("xodu2t/blings_playground");
      (async () => {
        const data = await fetchUserAttributes();
        // const data = await Auth.currentUserInfo();
        LogRocket.identify("USER_IN_APP_ID", {
          name: data.sub as string,
          email: data.email as string,
          id: data.sub as string,
        });
      })();
    }
  }, []);

  return (
    <ConfigProvider theme={BlingsTheme} wave={{ disabled: true }}>
      <Layout>
        {projectId && <TopBar />}
        <MainLayout />
      </Layout>
      <ToastContainer
        position="bottom-left"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ConfigProvider>
  );
};
const AppWithRouter = () => {
  useEffect(() => {
    (async () => {})();
  }, []);
  return (
    <Provider value={rootStore}>
      <Router>
        <Route
          path={"/:projectId?/:scene?"}
          component={withDefaultSignIn(withSetupTOTPMFA(App), {
            config: {
              enableCookies: AUTH_COOKIE_SYNC_CONFIG,
              amplify: {
                Hub: Hub,
                getCurrentUser: getCurrentUser,
                signIn: signIn,
                signOut: signOut,
                get: get,
                post: post,
                del: del,
              },
              ui: {
                Authenticator: BrandedAuthenticator,
                ThemeProvider: ThemeProvider,
              },
              environment: getENV(),
            },
            infoData: {
              contact_email: info_data.contactMail,
              support: info_data.support,
            },
          })}
        />
      </Router>
    </Provider>
  );
};

export default AppWithRouter;
